import { minBy } from 'lodash-es'
import { Vector3 } from 'three'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

/**
 * Area loads are rectangles in the plane of their element: `origin` is P0, `l1` the length of
 * the edge P0 → P1 (along `u`) and `l2` the length of the edge P1 → P2 (along `v`).
 */
export interface AreaLoadRectangle {
  origin: Vector3
  u: Vector3
  v: Vector3
  l1: number
  l2: number
}

export interface AreaLoadPoint {
  x: number
  y: number
  z: number
}

const MIN_LENGTH = 1e-9

const toVector = ({ x, y, z }: AreaLoadPoint) => new Vector3(x, y, z)

/** Plane normal of a polygon (Newell's method), pointing upwards. */
const getPolygonNormal = (points: Vector3[]): Vector3 => {
  const normal = new Vector3()

  points.forEach((current, index) => {
    const next = points[(index + 1) % points.length]
    normal.x += (current.y - next.y) * (current.z + next.z)
    normal.y += (current.z - next.z) * (current.x + next.x)
    normal.z += (current.x - next.x) * (current.y + next.y)
  })

  normal.normalize()

  return normal.z < 0 ? normal.negate() : normal
}

/**
 * Reads the rectangle of an area load. Only P1, P2 and P3 are used (P0 = P1 - l1 * u), so the
 * rectangle stays intact while P0 is being edited. Returns undefined for shapes that are not
 * quadrilaterals.
 */
export const toAreaLoadRectangle = (points: AreaLoadPoint[]): AreaLoadRectangle | undefined => {
  if (points.length !== 4 || points.some(point => !point)) return undefined

  const [, p1, p2, p3] = points.map(toVector)

  const edge1 = p2.clone().sub(p3)
  const edge2 = p2.clone().sub(p1)
  const l1 = edge1.length()
  const l2 = edge2.length()

  if (l1 < MIN_LENGTH || l2 < MIN_LENGTH) return undefined

  const u = edge1.divideScalar(l1)
  // drop any component along u so that the rebuilt shape is a proper rectangle
  const v = edge2.sub(u.clone().multiplyScalar(edge2.dot(u))).normalize()

  return { origin: p1.clone().addScaledVector(u, -l1), u, v, l1, l2 }
}

export const toAreaLoadRectanglePoints = ({
  origin,
  u,
  v,
  l1,
  l2,
}: AreaLoadRectangle): AreaLoadPoint[] =>
  [
    origin.clone(),
    origin.clone().addScaledVector(u, l1),
    origin.clone().addScaledVector(u, l1).addScaledVector(v, l2),
    origin.clone().addScaledVector(v, l2),
  ].map(({ x, y, z }) => ({ x, y, z }))

export const toAreaOfAttack = (rectangle: AreaLoadRectangle): Polygon => ({
  points: toAreaLoadRectanglePoints(rectangle).map(({ x, y, z }) => new ImmutableVector3(x, y, z)),
})

/**
 * Smallest rectangle in the plane of `points` that covers them, aligned with the edge P0 → P1.
 * For a rectangular element this is the element itself.
 */
export const getBoundingAreaLoadRectangle = (
  points: AreaLoadPoint[],
): AreaLoadRectangle | undefined => {
  if (points.length < 3) return undefined

  const vectors = points.map(toVector)
  const [p0, p1] = vectors

  const u = p1.clone().sub(p0)

  if (u.length() < MIN_LENGTH) return undefined

  u.normalize()

  const v = getPolygonNormal(vectors).cross(u).normalize()

  const alongU = vectors.map(point => point.clone().sub(p0).dot(u))
  const alongV = vectors.map(point => point.clone().sub(p0).dot(v))

  const minU = Math.min(...alongU)
  const minV = Math.min(...alongV)

  return {
    origin: p0.clone().addScaledVector(u, minU).addScaledVector(v, minV),
    u,
    v,
    l1: Math.max(...alongU) - minU,
    l2: Math.max(...alongV) - minV,
  }
}

/** Moves the rectangle so that P0 is at (x, y); z follows the plane of the rectangle. */
export const moveAreaLoadRectangleOrigin = (
  rectangle: AreaLoadRectangle,
  x: number,
  y: number,
): AreaLoadRectangle => {
  const { origin, u, v } = rectangle
  const normal = u.clone().cross(v)
  const z = origin.z - (normal.x * (x - origin.x) + normal.y * (y - origin.y)) / normal.z

  return { ...rectangle, origin: new Vector3(x, y, z) }
}

// CLIPPING

// Largest distance (m) an edge of an area load is moved to fit it onto its element
export const MAX_CLIP_DISTANCE = 0.01
// Clipped loads end this far (m) inside of the element edges, so that the float noise of the
// transformations in the backend validation does not put them outside of the element again
const CLIP_INSET = 1e-9
// Largest distance (m) of an element corner from the plane of the load that is still clipped
const MAX_PLANE_DISTANCE = 1e-3
const MAX_ITERATIONS = 16

type Point2 = [number, number]
// [lower, upper] corner of a rectangle in the frame of the area load (s along u, t along v)
type Bounds = [Point2, Point2]
type Axis = 0 | 1
type Side = 0 | 1
interface EdgeMove {
  shift: number
  axis: Axis
  side: Side
  bound: number
}

const cross2 = (o: Point2, a: Point2, b: Point2) =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])

const getSignedArea = (polygon: Point2[]) =>
  polygon.reduce((area, point, index) => {
    const next = polygon[(index + 1) % polygon.length]
    return area + (point[0] * next[1] - next[0] * point[1]) / 2
  }, 0)

/** Counter-clockwise convex polygon without collinear corners, or undefined if not convex. */
const toConvexPolygon = (points: Point2[]): Point2[] | undefined => {
  const ccw = getSignedArea(points) < 0 ? [...points].reverse() : points
  const corners = ccw.filter((point, index) => {
    const previous = ccw[(index + ccw.length - 1) % ccw.length]
    const next = ccw[(index + 1) % ccw.length]
    return Math.abs(cross2(previous, point, next)) > 1e-12
  })

  if (corners.length < 3) return undefined

  const isConvex = corners.every(
    (point, index) =>
      cross2(
        corners[(index + corners.length - 1) % corners.length],
        point,
        corners[(index + 1) % corners.length],
      ) > 0,
  )

  return isConvex ? corners : undefined
}

/** Moves every edge of the counter-clockwise convex `polygon` inwards by `distance`. */
const insetConvexPolygon = (polygon: Point2[], distance: number): Point2[] | undefined => {
  // each edge as the line n · x = c with its inward normal n
  const lines = polygon.map((point, index) => {
    const next = polygon[(index + 1) % polygon.length]
    const length = Math.hypot(next[0] - point[0], next[1] - point[1])
    const normal: Point2 = [-(next[1] - point[1]) / length, (next[0] - point[0]) / length]
    return { normal, c: normal[0] * point[0] + normal[1] * point[1] + distance }
  })

  const inset = lines.map((line, index): Point2 => {
    const previous = lines[(index + lines.length - 1) % lines.length]
    const determinant = previous.normal[0] * line.normal[1] - previous.normal[1] * line.normal[0]
    return [
      (previous.c * line.normal[1] - line.c * previous.normal[1]) / determinant,
      (previous.normal[0] * line.c - line.normal[0] * previous.c) / determinant,
    ]
  })

  // the inset of a convex polygon stays convex unless it collapses
  return getSignedArea(inset) > 0 ? inset : undefined
}

const isInConvexPolygon = (polygon: Point2[], point: Point2) =>
  polygon.every(
    (corner, index) => cross2(corner, polygon[(index + 1) % polygon.length], point) >= 0,
  )

/** Interval of the convex `polygon` on the line through `point` along `axis`. */
const getIntervalAlongAxis = (
  polygon: Point2[],
  point: Point2,
  axis: Axis,
): [number, number] | undefined => {
  const other = axis === 0 ? 1 : 0
  const crossings = polygon.flatMap((corner, index) => {
    const next = polygon[(index + 1) % polygon.length]
    const low = Math.min(corner[other], next[other])
    const high = Math.max(corner[other], next[other])

    if (point[other] < low || point[other] > high) return []
    if (high === low) return [corner[axis], next[axis]]

    const ratio = (point[other] - corner[other]) / (next[other] - corner[other])
    return [corner[axis] + ratio * (next[axis] - corner[axis])]
  })

  return crossings.length ? [Math.min(...crossings), Math.max(...crossings)] : undefined
}

/**
 * Edge moves that put the corner on the `sides` of the rectangle `bounds` into the convex
 * `polygon`. Empty if the corner already is inside, undefined if no single move does.
 */
const getCornerMoves = (
  polygon: Point2[],
  bounds: Bounds,
  sides: [Side, Side],
): EdgeMove[] | undefined => {
  const corner: Point2 = [bounds[sides[0]][0], bounds[sides[1]][1]]
  const moves: EdgeMove[] = []

  for (const axis of [0, 1] as const) {
    const interval = getIntervalAlongAxis(polygon, corner, axis)

    if (!interval) continue

    const [low, high] = interval

    if (low <= corner[axis] && corner[axis] <= high) return []

    const side = sides[axis]

    if (side === 0 && corner[axis] < low) {
      moves.push({ shift: low - corner[axis], axis, side, bound: low })
    } else if (side === 1 && corner[axis] > high) {
      moves.push({ shift: corner[axis] - high, axis, side, bound: high })
    }
  }

  return moves.length ? moves : undefined
}

const isEmpty = ([lower, upper]: Bounds) => lower[0] >= upper[0] || lower[1] >= upper[1]

/**
 * Moves the edges of the rectangle `bounds` inwards until it lies within the convex `polygon`,
 * always moving the edge that needs the smallest shift. Undefined if that fails.
 */
const clipBoundsToPolygon = (polygon: Point2[], original: Bounds): Bounds | undefined => {
  const xs = polygon.map(([x]) => x)
  const ys = polygon.map(([, y]) => y)
  // within the bounding box of the polygon, the lines through the corners cut the polygon
  const bounds: Bounds = [
    [Math.max(original[0][0], Math.min(...xs)), Math.max(original[0][1], Math.min(...ys))],
    [Math.min(original[1][0], Math.max(...xs)), Math.min(original[1][1], Math.max(...ys))],
  ]

  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
    if (isEmpty(bounds)) return undefined

    const moves: EdgeMove[] = []

    for (const sides of [
      [0, 0],
      [1, 0],
      [1, 1],
      [0, 1],
    ] as [Side, Side][]) {
      const cornerMoves = getCornerMoves(polygon, bounds, sides)

      if (!cornerMoves) return undefined

      moves.push(...cornerMoves)
    }

    if (!moves.length) return bounds

    const { axis, side, bound } = minBy(moves, 'shift') as EdgeMove
    bounds[side][axis] = bound
  }

  return undefined
}

/**
 * Clips an area load that slightly overshoots its element (snapping, almost rectangular
 * elements, float noise) onto the element: the edges of the load rectangle are moved inwards
 * onto the edges of the element (`CLIP_INSET` inside of them), as long as no edge moves more than
 * `MAX_CLIP_DISTANCE`. Returns the area unchanged if it is already contained, is no rectangle,
 * or cannot be clipped like that, the backend then rejects the load.
 */
export const clipAreaOfAttackToElement = (
  areaOfAttack: Polygon,
  elementPoints: AreaLoadPoint[],
): Polygon => {
  const rectangle = toAreaLoadRectangle(areaOfAttack.points)

  if (!rectangle) return areaOfAttack

  const { origin, u, v, l1, l2 } = rectangle
  const normal = u.clone().cross(v)
  const elementInFrame = elementPoints.map(point => toVector(point).sub(origin))

  if (elementInFrame.some(point => Math.abs(point.dot(normal)) > MAX_PLANE_DISTANCE)) {
    return areaOfAttack
  }

  const element = toConvexPolygon(
    elementInFrame.map((point): Point2 => [point.dot(u), point.dot(v)]),
  )
  const target = element && insetConvexPolygon(element, CLIP_INSET)

  if (!target) return areaOfAttack

  const original: Bounds = [
    [0, 0],
    [l1, l2],
  ]
  const corners: Point2[] = [
    [0, 0],
    [l1, 0],
    [l1, l2],
    [0, l2],
  ]

  if (corners.every(corner => isInConvexPolygon(target, corner))) return areaOfAttack

  const clipped = clipBoundsToPolygon(target, original)

  if (!clipped) return areaOfAttack

  const edgeShifts = [
    clipped[0][0] - original[0][0],
    clipped[0][1] - original[0][1],
    original[1][0] - clipped[1][0],
    original[1][1] - clipped[1][1],
  ]

  if (edgeShifts.some(shift => shift > MAX_CLIP_DISTANCE + CLIP_INSET)) return areaOfAttack

  const [[lowerS, lowerT], [upperS, upperT]] = clipped

  return toAreaOfAttack({
    origin: origin.clone().addScaledVector(u, lowerS).addScaledVector(v, lowerT),
    u,
    v,
    l1: upperS - lowerS,
    l2: upperT - lowerT,
  })
}
